<template>
  <div>
    <organization-breadcrumb :items="[$t('menu.learners')]" />
    <el-main class="student-details">
      <el-row justify="center" class="heading">
        <el-col :md="8" :xs="24" :sm="24">
          <h1 class="hidden-xs-only">{{ fullName }}</h1>
        </el-col>
        <el-col :md="14" :xs="24">
          <date-interval-picker
            :startDate="startDate"
            :endDate="endDate"
            @updatedDate="setNewDate"
            @update:scope="updateScope"
            :timelineLabel="timelineLabel || this.$t('date_interval_picker.this_month')"
          />
        </el-col>
      </el-row>
      <el-row type="flex" :gutter="32" class="details">
        <el-col :md="8" :xs="24" :sm="24">
          <elsa-section :title="$t('student_info.profile')">
            <template v-slot:body>
              <initial-avatar
                v-bind="$attrs"
                :avatarUrl="currentStudent.photo_url"
                size="large"
                :userObject="currentStudent"
              />
              <info-row :label="$t('teacher_info.first_name')" :value="currentStudent.first_name" />
              <info-row :label="$t('teacher_info.last_name')" :value="currentStudent.last_name" />
              <info-row :label="$t('teacher_info.email')" :value="currentStudent.email" />
              <info-row :label="$t('teacher_info.username')" :value="currentStudent.username" />
              <info-row :label="$t('teacher_info.phone_number')" :value="currentStudent.phone_number || ''" />
            </template>
          </elsa-section>
          <elsa-section :title="groupsSectionTitle" class="spacer class-list">
            <template v-slot:body>
              <h4 v-if="numberOfClasses === 0">{{ $t("student_detail.no_classes_available") }}</h4>
              <slot v-else>
                <div v-for="(clazz, idx) in studentClassesSample" :key="idx">
                  <p>
                    {{ clazz.name }}
                  </p>
                  <class-progress :classData="clazz" />
                </div>
              </slot>
            </template>
          </elsa-section>
        </el-col>
        <el-col :md="16" :xs="24">
          <elsa-section class="overview" :title="$t('student_info.overview')">
            <template v-slot:body>
              <el-row type="flex" :gutter="10">
                <el-col :md="12" :xs="24" :span="12">
                  <icon-info-box
                    v-if="lastEps || userEps"
                    icon="average-score"
                    :value="(lastEps || userEps) | toPercentage"
                    :label="$t('student_detail.last_eps')"
                    showListText="Detail"
                    :showListAction="handleShowLessonsDetail"
                  />
                </el-col>
                <el-col :md="12" :xs="24" :span="12">
                  <icon-info-box
                    v-if="additionalScore > 0"
                    icon="average-score"
                    :value="additionalScore"
                    :label="$t('student_detail.assessment_test')"
                    showListText="History"
                    :showListAction="handleShowAssessment"
                  />
                  <icon-info-box
                    v-else-if="score"
                    icon="average-score"
                    :value="score | toPercentage"
                    :label="$t('student_detail.assessment_test')"
                    showListText="History"
                    :showListAction="handleShowAssessment"
                  />
                </el-col>
                <el-col :md="12" :xs="24" :span="12">
                  <icon-info-box
                    icon="goals-minutes-day"
                    :value="numberOfMinutesPerDay"
                    :label="$t('common.minutes_per_day')"
                  />
                </el-col>
                <el-col :md="12" :xs="24" :span="12">
                  <icon-info-box
                    icon="goals-days-practice"
                    :value="numberOfDaysPractice"
                    :label="$t('classes.classes_details.overview_section.days_practice_per_week')"
                  />
                </el-col>
              </el-row>
            </template>
          </elsa-section>
          <elsa-section
            class="spacer chart-mobile"
            :title="$t('classes.classes_details.activity_reports.number_of_lessons_practiced')"
            icon="elsa-icon-chart-students"
            :loading="isLoadingChart"
          >
            <template v-slot:body>
              <mixed-chart :series="lessonsTotalMinutesPracticed" :labels="dateLabels" />
            </template>
          </elsa-section>
          <!-- <elsa-section class="spacer chart-mobile">
            <template v-slot:body>
              <time-spent-chart
                :labels="dateLabels"
                :series="lessonsTimeSeries"
                :titleY="$t('common.minutes')"
                :titleYOpposite="$t('charts.time_spent.number_of_lessons')"
                :information="$t('charts.time_spent.tooltip.lessons')"
                barColorStop1="#252259"
                barColorStop2="#8873EF"
              />
            </template>
          </elsa-section>
          <elsa-section class="spacer chart-mobile">
            <template v-slot:body>
              <time-spent-chart
                :labels="dateLabels"
                :series="studySetTimeSeries"
                :titleY="$t('common.minutes')"
                :titleYOpposite="$t('charts.time_spent.number_of_studysets')"
                :information="$t('charts.time_spent.tooltip.studysets')"
                barColorStop1="#713FF4"
                barColorStop2="#CD55FF"
              />
            </template>
          </elsa-section> -->
          <elsa-section
            class="spacer chart-mobile"
            :title="$t('classes.classes_details.activity_reports.progress_student')"
            icon="elsa-icon-chart-students"
            :loading="isLoadingChart"
          >
            <template v-slot:body>
              <line-chart :series="eps" :labels="dateLabels" />
            </template>
          </elsa-section>
          <elsa-section :title="$t(`charts.pentagon_title`)" v-if="canShowPentagon" :loading="isLoadingChart">
            <template v-slot:body>
              <!-- <p class="opa-75 section-description">
                {{ $t(`charts.pentagon_desc`) }}
              </p> -->
              <pentagon :score="pentagon" />
            </template>
          </elsa-section>
          <elsa-section :title="$t(`charts.ielts_title`)" v-if="ieltsScore > 0" :loading="isLoadingChart">
            <template v-slot:body>
              <p class="opa-75 section-description">
                {{ $t("charts.ielts_desc") }}
              </p>
              <semi-radial-bar-chart :series="[ieltsScore]" />
            </template>
          </elsa-section>
          <elsa-section :title="$t('charts.highest_average_score_skills_title')" :loading="isLoadingChart">
            <template v-slot:body>
              <div class="opa-75 section-description flex">
                <span class="flex content-center"> <i class="elsa-icon-chart-sound-game"/></span>
                <p class="score_skills_decs">{{ $t("charts.highest_average_score_skills_desc") }}</p>
              </div>
              <pronunciation-skill-list :series="highestAverageScoreSkills" />
            </template>
          </elsa-section>
          <elsa-section :title="$t('charts.lowest_average_score_skills_title')" :loading="isLoadingChart">
            <template v-slot:body>
              <div class="opa-75 section-description flex">
                <span class="flex content-center"> <i class="elsa-icon-chart-sound-game"/></span>
                <p class="score_skills_decs">{{ $t("charts.lowest_average_score_skills_desc") }}</p>
              </div>
              <pronunciation-skill-list :series="lowestAverageScoreSkills" />
            </template>
          </elsa-section>
        </el-col>
      </el-row>
      <assessments-history
        :title="$t('common.assessment-history')"
        :dialogVisible="showAssessmentModal"
        @closed="showAssessmentModal = false"
        :assessments="assessmentsUser"
      />
      <lessons-detail
        :title="$t('common.lesson_detail')"
        :dialogVisible="showLessonDetailModal"
        @closed="showLessonDetailModal = false"
        :lessons="lessonsDetailUser"
      />
      <delete-organization-user
        :dialogVisible="showRemoveAccountModal"
        v-on:closed="showRemoveAccountModal = false"
        @doRemoveStudentFromOrg="handelRemoveFromOrganization"
      />
    </el-main>
  </div>
</template>

<script>
import DeleteOrganizationUser from "@/components/modals/organizations/DeleteOrganizationUsers";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import ElsaSection from "@/components/common/ElsaSection";
import InfoRow from "@/components/common/InfoRow";
import IconInfoBox from "@/components/common/IconInfoBox";
import DateIntervalPicker from "@/components/common/DateIntervalPicker";
import LineChart from "@/components/common/charts/LineChart";
import MixedChart from "@/components/common/charts/MixedChart";
// import TimeSpentChart from '@/components/common/charts/TimeSpentChart'
import Pentagon from "@/components/common/charts/Pentagon";
import ClassProgress from "@/components/common/ClassProgress";
import { mapGetters, mapState, mapActions } from "vuex";
import { compose, pathOr, slice, propEq, lensProp, propOr, set, isNil, unless, last } from "ramda";
import { startOfMonth, format, toDate } from "date-fns";
import { warningMessage, successMessage } from "../utils/messages";
import percentage from "../components/common/mixins/percentage";
import showNoReportDataWarning from "@/components/common/mixins/noReportData";
import { isSessionExpireError } from "@/utils/sessionExpireErr";
import { lensMatching } from "../utils/ramda-utils";
import InitialAvatar from "@/components/common/InitialAvatar";
import AssessmentsHistory from "@/components/modals/reports/AssessmentsHistory";
import LessonsDetail from "@/components/modals/reports/LessonsDetail";
import { locale_mapping_date } from "@/helpers/languages";
import { ADMIN, TEACHER, STUDENT } from "../constants/userRoles";
import PronunciationSkillList from "@/components/common/charts/PronunciationSkillList";
import SemiRadialBarChart from "@/components/common/charts/SemiRadialBarChart";
import { PURPLE, PINK } from "@/constants/charts";
import { weeklyDateInterval, monthlyDateInterval, dailyDateInterval } from "@/utils/dateInterval";
import { Loading } from "element-ui";

const lensByName = compose(lensMatching, propEq("name"));

export default {
  mixins: [percentage, showNoReportDataWarning],
  components: {
    ClassProgress,
    ElsaSection,
    InfoRow,
    IconInfoBox,
    DateIntervalPicker,
    LineChart,
    MixedChart,
    // TimeSpentChart,
    Pentagon,
    InitialAvatar,
    AssessmentsHistory,
    LessonsDetail,
    DeleteOrganizationUser,
    OrganizationBreadcrumb,
    PronunciationSkillList,
    SemiRadialBarChart,
  },
  props: {
    studentId: {},
    organizationId: {},
    backTo: {
      default() {
        return () => this.defaultBackToUsers();
      },
    },
  },
  data() {
    return {
      startDate: startOfMonth(Date.now()),
      endDate: toDate(Date.now()),
      scope: "daily",
      showAssessmentModal: false,
      showLessonDetailModal: false,
      showRemoveAccountModal: false,
      purple: PURPLE,
      pink: PINK,
      isLoadingChart: false,
    };
  },
  computed: {
    ...mapGetters("user", ["isElsaAdmin"]),
    ...mapState("students", [
      "eps",
      "lessonsTotalTimePracticed",
      // 'lessonsTimePracticed',
      // 'studySetTimePracticed',
      "studentClasses",
      "overviewData",
      "assessmentData",
      "highestAverageScoreSkills",
      "lowestAverageScoreSkills",
      "pentagon",
      "ielts",
    ]),
    ...mapState("classes", ["assessmentsUser", "lessonsDetailUser"]),
    lessonsTotalMinutesPracticed: compose(
      set(compose(lensByName("number_of_lessons"), lensProp("type")), "column"),
      set(compose(lensByName("number_of_seconds"), lensProp("type")), "line"),
      propOr([], "lessonsTotalTimePracticed")
    ),
    score: compose(unless(isNil, Math.round), pathOr(null, ["assessmentData", "assessment_test"])),
    additionalScore: compose(unless(isNil, Math.round), pathOr(null, ["assessmentData", "additional_assessment_test"])),
    lastEps: compose(unless(isNil, Math.round), last(), pathOr([], ["eps", 0, "data"])),
    ieltsScore() {
      const value = pathOr(0, ["ielts", "ielts"])(this);
      return value * 10;
    },
    numberOfClasses() {
      return pathOr(0, ["studentClasses", "count"])(this);
    },
    studentClassesSample() {
      return compose(slice(0, 3), pathOr([], ["studentClasses", "results"]))(this);
    },
    dateLabels() {
      if (this.scope === "weekly") {
        return weeklyDateInterval(this.startDate, this.endDate);
      } else if (this.scope === "monthly") {
        return monthlyDateInterval(this.startDate, this.endDate);
      }

      return dailyDateInterval(this.startDate, this.endDate);
    },
    numberOfDaysPractice: compose(Math.round, pathOr(0, ["overviewData", "days_practice"])),
    numberOfMinutesPerDay: compose(Math.round, pathOr(0, ["overviewData", "minutes_per_day"])),
    userEps: compose(Math.round, pathOr(0, ["currentStudent", "eps"])),
    ...mapGetters({
      currentOrganizationId: "organization/currentOrganizationId",
      currentOrganizationType: "organization/currentOrganizationType",
      userHasOrganizationRole: "organization/userHasOrganizationRole",
      currentStudent: "students/currentStudent",
      loadingAverageScoreSkills: "students/loadingAverageScoreSkills",
      currentChartDate: "students/currentChartDate",
    }),
    fullName() {
      return `${this.currentStudent.first_name} ${this.currentStudent.last_name}`;
    },
    isStudent() {
      return (
        !this.userHasOrganizationRole(ADMIN) &&
        !this.userHasOrganizationRole(TEACHER) &&
        this.userHasOrganizationRole(STUDENT)
      );
    },
    groupsSectionTitle() {
      const groupTranslation = this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, "groups"));
      return `${groupTranslation} (${this.numberOfClasses})`;
    },
    timelineLabel() {
      const formatedStartDate = format(new Date(this.startDate), "dd MMM yyyy", {
        locale: locale_mapping_date[this.$i18n.locale],
      });
      const formatedEndDate = format(new Date(this.endDate), "dd MMM yyyy", {
        locale: locale_mapping_date[this.$i18n.locale],
      });
      return `${formatedStartDate} - ${formatedEndDate}`;
    },
    canShowPentagon() {
      const isShow =
        this.pentagon &&
        this.pentagon.pronunciation >= 0 &&
        this.pentagon.fluency >= 0 &&
        this.pentagon.intonation >= 0 &&
        this.pentagon.word_stress >= 0 &&
        this.pentagon.listening >= 0;
      return isShow;
    },
  },
  methods: {
    getAllChartData(options) {
      this.isLoadingChart = true;

      return this.getChartData(options)
        .catch(err => {
          if (err.message === "Student ID is null or undefined") {
            // console.error(err);
          } else if (!isSessionExpireError(err)) {
            this.showNoReportDataWarning();
          }
        })
        .finally(() => {
          this.isLoadingChart = false;
        });
    },
    defaultBackToUsers() {
      return this.$router.push({
        name: "students",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    setNewDate(dateObject) {
      this.startDate = dateObject.startDate;
      this.endDate = dateObject.endDate;
      // this.timelineLabel = dateObject.timelineLabel

      const options = {
        from: this.startDate,
        to: this.endDate,
        scope: this.scope,
      };

      this.setChartDate({
        startDate: dateObject.startDate,
        endDate: dateObject.endDate,
      });

      this.getAllChartData(options);
    },
    updateScope(newScope) {
      this.scope = newScope;
    },
    ...mapActions("students", ["getChartData", "getStudentClasses", "getStudent", "setChartDate", "resetChartDate"]),
    ...mapActions({
      removeFromOrganization: "organization/removeFromOrganization",
      getAssessmentUser: "classes/getAssessmentUser",
      getLessonsDetailUser: "classes/getLessonsDetailUser",
      removeUserFromSystem: "user/removeUserFromSystem",
    }),
    handelRemoveFromOrganization(isRemoveUserFromSystem = false) {
      this.removeFromOrganization({ userId: this.studentId, organizationId: this.organizationId })
        .then(() => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, "remove_from_organization_success");
          successMessage(this.$t(message))(this);
          // Check if remove user from database
          if (isRemoveUserFromSystem) {
            this.removeUserFromSystem({ studentId: this.studentId })
              .then(() => {
                const message = this.$t("common.remove_user_from_system_success");
                successMessage(this.$t(message))(this);
              })
              .catch(error => {
                const message = this.$t("common.remove_user_from_system_warning");
                warningMessage(this.$t(message))(this);
              });
          }
          this.backTo();
        })
        .catch(error => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, "remove_from_organization_warning");
          return warningMessage(this.$t(message))(this);
        });
    },
    async handleShowAssessment() {
      this.showAssessmentModal = true;
      try {
        await this.getAssessmentUser({
          student: this.currentStudent,
          from: this.startDate,
          to: this.endDate,
        });
      } catch {
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    async handleShowLessonsDetail() {
      this.showLessonDetailModal = true;
      try {
        await this.getLessonsDetailUser({
          student: this.currentStudent,
          from: this.startDate,
          to: this.endDate,
        });
      } catch {
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
  },
  async mounted() {
    const loadingInstance = Loading.service({ fullscreen: true });

    try {
      await Promise.all([
        this.getStudent({
          organizationId: this.organizationId,
          studentId: this.studentId,
        }),
        this.getStudentClasses({
          organizationId: this.organizationId,
          studentId: this.studentId,
        }),
      ]);
    } catch {
      loadingInstance.close();
      return warningMessage(this.$t("common.generic_error_message"))(this);
    }

    this.setNewDate(this.currentChartDate);
    loadingInstance.close();
  },
  watch: {
    scope(newValue, oldValue) {
      if (newValue !== oldValue) {
        const options = {
          from: this.startDate,
          to: this.endDate,
          scope: this.scope,
        };
        this.getAllChartData(options);
      }
    },
  },
  beforeDestroy() {
    this.resetChartDate();
  },
};
</script>

<style lang="scss" scoped>
.heading {
  margin-top: 42px;
  & > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

img {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: 26px;
}

.info-row {
  margin-bottom: 26px;
  &:last-of-type {
    margin-bottom: 0;
  }
}

.details {
  margin-top: 52px;
}

.delete {
  display: flex;
  font-weight: 600;
  font-size: 14px;
  text-transform: capitalize;
  color: #546873;
  line-height: 48px;
  margin-top: 32px;
  cursor: pointer;
  i {
    margin-right: 15px;
    &:after {
      margin-left: 0;
    }
  }
}
.overview::v-deep .section-body {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.elsa-section {
  padding: 32px;
}

.class-list {
  ::v-deep .section-body {
    margin-top: 14px;
    p {
      margin-top: 30px;
      margin-bottom: 24px;
    }
  }

  ::v-deep .icon-info-box {
    min-width: 50%;
    margin-bottom: 32px;

    .info {
      width: 100%;
    }
  }
}

.overview::v-deep .icon-info-box {
  margin-bottom: 32px;
}

.section-description {
  font-size: 18px;
  line-height: 28px;
  font-weight: 400;
  margin-top: 8px;
}

.score_skills_decs {
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
}

@media only screen and (max-width: 768px) {
  .chart-mobile {
    overflow-x: scroll;
  }
}
</style>
